import React, { useState, useEffect } from "react";
import { displayToast } from "../utils/displayToast";
import { useErrorHandler } from "../utils/ErrorHandler";
import { fetchRoles, deleteRole, fetchMenus } from "../apis/role";
import { fetchDepartments } from "../apis/department";
import RoleFormPopup from "./RoleFormPopup";
import "../styles/components/UserManagement.css";
import "../styles/components/GroupManagement.css";
import "../styles/components/PageStyles.css";
import Search from "../assets/svgIcons/Search.svg";
import DropDown from "../assets/svgIcons/PrimaryDropDown.svg";
import { useOutsideClick } from "../utils/useOutsideClick";

const GroupManagement = () => {
  const { handleErrorResponse } = useErrorHandler();
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filteredRoles, setFilteredRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentRole, setCurrentRole] = useState(null);
  const [viewingRole, setViewingRole] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [permissionStructure, setPermissionStructure] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState({});
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const filters = [
    { title: "name", display_name: "Role Name" },
    { title: "can_access_admin_dashboard", display_name: "Admin Access" },
    { title: "status", display_name: "Status" },
  ];

  const dropdownRef = useOutsideClick(() => {
    setIsDropdownOpen(false);
  });

  const loadPermissions = async () => {
    try {
      const response = await fetchMenus();
      if (response.description) {
        displayToast("Failed to load permissions: " + response.description);
      } else {
        setPermissionStructure(response);
      }
    } catch (error) {
      displayToast("Error loading menus: " + error.message);
    }
  };

  const loadRoles = async () => {
    try {
      setIsLoading(true);
      const response = await fetchRoles();

      if (response && response.description) {
        handleErrorResponse(response.description);
      } else {
        setRoles(response || []);
        setFilteredRoles(response || []);
      }
    } catch (error) {
      displayToast("Failed to load roles: " + error.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const loadDepartments = async () => {
    try {
      const response = await fetchDepartments();
      if (response?.description) {
        handleErrorResponse(response.description);
      } else {
        setDepartments(Array.isArray(response) ? response : []);
      }
    } catch (error) {
      displayToast("Failed to load departments: " + error.message, "error");
    }
  };

  const handleSearch = () => {
    if (!selectedCategory.title) {
      displayToast("Please select a category first", "error");
      return;
    }

    if (!searchValue.trim()) {
      setFilteredRoles(roles);
      return;
    }

    const filtered = roles.filter((role) => {
      const fieldValue = role[selectedCategory.title];

      if (fieldValue === null || fieldValue === undefined) {
        return false;
      }

      if (typeof fieldValue === "boolean") {
        const searchLower = searchValue.toLowerCase();
        return searchLower === "yes"
          ? fieldValue
          : searchLower === "no"
          ? !fieldValue
          : false;
      }

      return String(fieldValue)
        .toLowerCase()
        .includes(searchValue.toLowerCase());
    });

    setFilteredRoles(filtered);
  };

  const handleViewRole = (role) => {
    setViewingRole(role);
  };

  const closeViewModal = () => {
    setViewingRole(null);
  };

  const getStatusBadgeClass = (status) => {
    const statusLower = String(status).toLowerCase();
    if (statusLower === "active") return "mfg-status-active";
    if (statusLower === "inactive") return "mfg-status-inactive";
    if (statusLower === "pending") return "mfg-status-pending";
    return "mfg-status-default";
  };

  useEffect(() => {
    if (selectedCategory.title && searchValue.trim()) {
      handleSearch();
    } else if (!searchValue.trim()) {
      setFilteredRoles(roles);
    }
  }, [searchValue, selectedCategory]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadPermissions();
    loadRoles();
    loadDepartments();

    setSelectedCategory(filters[0]);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setFilteredRoles(roles);
  }, [roles]);

  const getDepartmentName = (departmentId) =>
    departments.find((department) => String(department.id) === String(departmentId))
      ?.name || (departmentId ? `Department #${departmentId}` : "N/A");

  const openAddPopup = () => {
    setIsEditing(false);
    setCurrentRole(null);
    setShowPopup(true);
  };

  const openEditPopup = (role) => {
    const roleForEdit = {
      id: role.id,
      name: role.name,
      department_id: role.department_id,
      level: role.level,
      can_access_admin_dashboard: role.can_access_admin_dashboard || false,
      menu_controls: [
        ...(role.user_menu_controls || []),
        ...(role.admin_menu_controls || []),
      ],
    };

    setIsEditing(true);
    setCurrentRole(roleForEdit);
    setShowPopup(true);
  };

  const openDeleteConfirm = (id) => {
    setConfirmDeleteId(id);
    setShowDeleteConfirm(true);
  };

  const closePopup = () => setShowPopup(false);
  const closeDeleteConfirm = () => {
    setShowDeleteConfirm(false);
    setConfirmDeleteId(null);
  };

  const handleDeleteRole = async () => {
    if (!confirmDeleteId) return;
    try {
      setIsLoading(true);
      const response = await deleteRole(confirmDeleteId);
      if (response.status && !response.description) {
        displayToast("Role deleted successfully!", "success");
        await loadRoles();
      } else {
        handleErrorResponse(response?.description || "Failed to delete role");
      }
      closeDeleteConfirm();
    } catch (error) {
      displayToast("Error deleting role: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRoleSaved = async () => {
    await loadRoles();
    closePopup();
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setIsDropdownOpen(false);

    if (searchValue.trim()) {
      setSearchValue("");
      setFilteredRoles(roles);
    }
  };

  return (
    <div className="mfg-page-container">
      <div className="mfg-page-header">
        <div className="mfg-page-title-section">
          <h2 className="mfg-page-title">
            <span className="mfg-page-title-icon">🔐</span>
            Access Control
          </h2>
          <p className="mfg-page-subtitle">Manage roles and permissions</p>
        </div>
        <div className="mfg-header-actions">
          <button
            className="mfg-btn mfg-btn-primary"
            onClick={openAddPopup}
          >
            <span className="mfg-btn-icon">➕</span>
            Add Role
          </button>
        </div>
      </div>

      <div className="mfg-card">
        <div className="mfg-card-header">
          <h3 className="mfg-card-title">
            <span className="mfg-card-title-icon">📋</span>
            Roles List
          </h3>
        </div>

        <div className="mfg-filters">
          <div className="mfg-filter-dropdown" ref={dropdownRef}>
            <div
              className="mfg-filter-dropdown-btn"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
              <span>{selectedCategory.display_name || "Select Category"}</span>
              <img src={DropDown} alt="dropdown" className="mfg-filter-dropdown-icon" />
            </div>

            {isDropdownOpen && (
              <div className="mfg-filter-dropdown-menu">
                {filters.map((filter, index) => (
                  <div
                    key={index}
                    className="mfg-filter-dropdown-item"
                    onClick={() => handleCategorySelect(filter)}
                  >
                    {filter.display_name}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mfg-search-box">
            <input
              type="text"
              placeholder={`Search by ${selectedCategory.display_name || "role"}...`}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              className="mfg-search-input"
            />
            <img
              src={Search}
              alt="search"
              className="mfg-search-icon"
              onClick={handleSearch}
            />
          </div>
        </div>

        {isLoading ? (
          <div className="mfg-loading-state">
            <span className="loader"></span> Loading roles...
          </div>
        ) : filteredRoles.length > 0 ? (
          <div className="mfg-table-wrapper">
            <table className="mfg-table">
              <thead>
                <tr>
                  <th>Role Name</th>
                  <th>Status</th>
                  <th>Admin Dashboard Access</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRoles.map((role) => (
                  <tr key={role.id}>
                    <td><strong>{role.name}</strong></td>
                    <td>
                      <span className={`mfg-status-badge ${getStatusBadgeClass(role.status)}`}>
                        {role.status || "N/A"}
                      </span>
                    </td>
                    <td>
                      <span className={`mfg-status-badge ${role.can_access_admin_dashboard ? "mfg-status-active" : "mfg-status-inactive"}`}>
                        {role.can_access_admin_dashboard ? "Yes" : "No"}
                      </span>
                    </td>
                    <td>
                      <div className="mfg-table-actions">
                        <button
                          onClick={() => handleViewRole(role)}
                          className="mfg-btn mfg-btn-outline mfg-btn-sm"
                        >
                          👁️ <span className="mfg-btn-text">View</span>
                        </button>
                        <button
                          onClick={() => openEditPopup(role)}
                          className="mfg-btn mfg-btn-warning mfg-btn-sm"
                        >
                          ✏️ <span className="mfg-btn-text">Edit</span>
                        </button>
                        <button
                          onClick={() => openDeleteConfirm(role.id)}
                          className="mfg-btn mfg-btn-danger mfg-btn-sm"
                        >
                          🗑️ <span className="mfg-btn-text">Delete</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="mfg-empty-state">
            <div className="mfg-empty-icon">🔐</div>
            <h4 className="mfg-empty-title">No Roles Found</h4>
            <p className="mfg-empty-text">Create a new role to get started</p>
          </div>
        )}
      </div>

      {showPopup && (
        <RoleFormPopup
          isEditing={isEditing}
          currentRole={currentRole}
          permissionStructure={permissionStructure}
          onClose={closePopup}
          onSave={handleRoleSaved}
        />
      )}

      {viewingRole && (
        <div className="mfg-modal-overlay" onClick={closeViewModal}>
          <div className="mfg-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mfg-modal-header">
              <h3 className="mfg-modal-title">
                <span>🔐</span> Role Details
              </h3>
              <button className="mfg-modal-close" onClick={closeViewModal}>
                ✕
              </button>
            </div>
            <div className="mfg-modal-body">
              <div className="mfg-view-grid">
                <div className="mfg-view-item">
                  <div className="mfg-view-label">Role Name</div>
                  <div className="mfg-view-value">{viewingRole.name}</div>
                </div>
                <div className="mfg-view-item">
                  <div className="mfg-view-label">Department</div>
                  <div className="mfg-view-value">
                    {getDepartmentName(viewingRole.department_id)}
                  </div>
                </div>
                <div className="mfg-view-item">
                  <div className="mfg-view-label">Status</div>
                  <div className="mfg-view-value">
                    <span className={`mfg-status-badge ${getStatusBadgeClass(viewingRole.status)}`}>
                      {viewingRole.status || "N/A"}
                    </span>
                  </div>
                </div>
                <div className="mfg-view-item">
                  <div className="mfg-view-label">Admin Dashboard Access</div>
                  <div className="mfg-view-value">
                    <span className={`mfg-status-badge ${viewingRole.can_access_admin_dashboard ? "mfg-status-active" : "mfg-status-inactive"}`}>
                      {viewingRole.can_access_admin_dashboard ? "Yes" : "No"}
                    </span>
                  </div>
                </div>
                {viewingRole.user_menu_controls && viewingRole.user_menu_controls.length > 0 && (
                  <div className="mfg-view-item mfg-view-full-width">
                    <div className="mfg-view-label">User Menu Permissions</div>
                    <div className="mfg-view-value">
                      {viewingRole.user_menu_controls.map((ctrl, idx) => (
                        <span key={idx} className="mfg-permission-tag">
                          {ctrl.menu_name || ctrl.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {viewingRole.admin_menu_controls && viewingRole.admin_menu_controls.length > 0 && (
                  <div className="mfg-view-item mfg-view-full-width">
                    <div className="mfg-view-label">Admin Menu Permissions</div>
                    <div className="mfg-view-value">
                      {viewingRole.admin_menu_controls.map((ctrl, idx) => (
                        <span key={idx} className="mfg-permission-tag">
                          {ctrl.menu_name || ctrl.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="mfg-modal-footer">
              <button className="mfg-btn mfg-btn-outline" onClick={closeViewModal}>
                Close
              </button>
              <button 
                className="mfg-btn mfg-btn-warning" 
                onClick={() => {
                  closeViewModal();
                  openEditPopup(viewingRole);
                }}
              >
                ✏️ Edit Role
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="mfg-modal-overlay" onClick={closeDeleteConfirm}>
          <div className="mfg-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mfg-modal-header">
              <h3 className="mfg-modal-title">
                <span>⚠️</span> Confirm Delete
              </h3>
              <button className="mfg-modal-close" onClick={closeDeleteConfirm}>
                ✕
              </button>
            </div>
            <div className="mfg-modal-body">
              <p className="mfg-modal-confirm-text">
                Are you sure you want to delete this role?
              </p>
              <p className="mfg-modal-danger-text">
                ⚠️ This action cannot be undone.
              </p>
            </div>
            <div className="mfg-modal-footer">
              <button
                className="mfg-btn mfg-btn-outline"
                onClick={closeDeleteConfirm}
              >
                Cancel
              </button>
              <button
                className="mfg-btn mfg-btn-danger"
                onClick={handleDeleteRole}
              >
                {isLoading ? <span className="loader"></span> : "🗑️ Confirm Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupManagement;
